import { Button } from "@/design-system/components/react";
import { cn } from "@/design-system/components/react/utils";
import { FaArrowRight, FaBookmark, FaLayerGroup } from "../utils/icons";

export interface ListEntry {
  key: string;
  kind: "collection" | "saved" | "cohort";
  label: string;
  count: number | null;
  isActive: boolean;
  onSelect: () => void;
}

/** The landing page and switcher share the same option and selection contract. */
export function WorklistListOption({
  entry,
  compact = false,
  onSelect = entry.onSelect,
}: {
  entry: ListEntry;
  compact?: boolean;
  onSelect?: () => void;
}) {
  const Icon = entry.kind === "saved" ? FaBookmark : FaLayerGroup;
  return (
    <Button
      variant="ghost"
      onClick={onSelect}
      className={cn(
        "group h-auto w-full justify-start text-left whitespace-normal",
        compact
          ? "gap-3 px-3 py-2"
          : "gap-4 border border-border bg-bg-2 p-5 hover:border-border-strong",
        entry.isActive && "bg-signal-tint text-signal-text-tint",
      )}
    >
      {!compact && (
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xs border border-border bg-surface-2 text-fg-dim group-hover:text-signal-text-tint">
          <Icon aria-hidden="true" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block",
            compact ? "truncate text-xs" : "text-sm font-medium leading-relaxed text-fg",
          )}
        >
          {entry.label}
        </span>
        {!compact && entry.count !== null && (
          <span className="mt-1 block font-mono text-[11px] font-normal text-fg-dim">
            {entry.count.toLocaleString()} {entry.count === 1 ? "case" : "cases"}
          </span>
        )}
      </span>
      {compact ? (
        entry.count !== null && (
          <span className="shrink-0 font-mono text-[11px] tabular-nums text-fg-dim">
            {entry.count.toLocaleString()}
          </span>
        )
      ) : (
        <FaArrowRight
          aria-hidden="true"
          className="text-fg-faint transition-transform group-hover:translate-x-0.5 group-hover:text-fg"
        />
      )}
    </Button>
  );
}

export function WorklistListPicker({
  entries,
  loading,
  error,
  unavailable,
  onRetry,
  panelsFailed,
  onRetryPanels,
}: {
  entries: ListEntry[];
  loading: boolean;
  error: boolean;
  unavailable: boolean;
  onRetry: () => void;
  /** The device panel list failed: said beside the collections, which still open. */
  panelsFailed: boolean;
  onRetryPanels: () => void;
}) {
  const collections = entries.filter((entry) => entry.kind === "collection");
  const otherLists = entries.filter((entry) => entry.kind !== "collection");
  return (
    <div className="h-full overflow-y-auto bg-bg text-fg page-premium-alt">
      <section
        aria-labelledby="worklist-picker-title"
        className="mx-auto w-full max-w-4xl px-6 py-12 sm:px-10 sm:py-20"
      >
        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.2em] text-fg-dim">
          Worklist
        </p>
        <h1 id="worklist-picker-title" className="text-3xl font-medium tracking-tight sm:text-4xl">
          Open a worklist
        </h1>
        <p className="mt-3 text-sm text-fg-muted">Choose a list to view cases</p>
        <div className="mt-10 border-t border-border pt-6">
          {loading ? (
            <div role="status" aria-live="polite">
              <p className="mb-4 font-mono text-xs text-fg-dim">Loading lists…</p>
              <div aria-hidden="true" className="grid gap-3 sm:grid-cols-2">
                {Array.from({ length: 4 }, (_, index) => (
                  <div
                    key={index}
                    className="h-24 rounded-xs border border-border bg-bg-2 p-5 motion-safe:animate-pulse"
                  >
                    <div className="h-3 w-2/3 bg-surface-2" />
                    <div className="mt-3 h-2 w-1/4 bg-surface-2" />
                  </div>
                ))}
              </div>
            </div>
          ) : error ? (
            <div role="alert" className="rounded-xs border border-border-strong bg-bg-2 p-6">
              <p className="text-sm text-fg">Couldn't load available lists</p>
              <p className="mt-2 text-sm text-fg-muted">Try loading them again.</p>
              <Button variant="outline" onClick={onRetry} className="mt-5">
                Retry loading lists
              </Button>
            </div>
          ) : (
            <>
              {unavailable && (
                <p role="status" className="mb-6 text-sm text-fg-muted">
                  This list is unavailable. Choose another list.
                </p>
              )}
              {panelsFailed && (
                <div
                  role="alert"
                  className="mb-6 flex items-center justify-between gap-4 rounded-xs border border-border-strong bg-bg-2 px-4 py-3"
                >
                  <p className="text-sm text-fg-muted">Couldn't load device panels</p>
                  <Button variant="outline" size="sm" onClick={onRetryPanels}>
                    Retry loading panels
                  </Button>
                </div>
              )}
              <div className="mb-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-fg-dim">
                <span>Collections</span>
                <span>{collections.length} available</span>
              </div>
              {collections.length > 0 ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {collections.map((entry) => (
                    <WorklistListOption key={entry.key} entry={entry} />
                  ))}
                </div>
              ) : (
                <p className="py-6 text-sm text-fg-muted">No collections are available.</p>
              )}
              {otherLists.length > 0 && (
                <details className="mt-8 border-t border-border pt-5">
                  <summary className="cursor-pointer text-xs text-fg-muted hover:text-fg">
                    Other lists{" "}
                    <span className="ml-2 font-mono text-fg-dim">{otherLists.length}</span>
                  </summary>
                  <div className="mt-4 max-h-72 overflow-y-auto rounded-xs border border-border bg-bg-2">
                    {otherLists.map((entry) => (
                      <WorklistListOption key={entry.key} entry={entry} compact />
                    ))}
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
